import Swal from 'sweetalert2';

// Version check interval in milliseconds (60 seconds)
const VERSION_CHECK_INTERVAL_MS = 60 * 1000;

// Track if we've already shown the update dialog
let updateDialogShown = false;

// Track the timer ID
let versionCheckTimerId: ReturnType<typeof setTimeout> | null = null;

// Track if version check is running
let isRunning = false;

/**
 * Check if we're running on a live .com domain
 */
const isLiveDomain = (): boolean => {
    return location.hostname.endsWith('.com');
};

/**
 * Extract script src attributes from an HTML string's head section
 */
const extractScriptSrcs = (html: string): string[] => {
    const scripts: string[] = [];
    
    // Find the head section
    const headMatch = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
    if (!headMatch) return scripts;
    
    const headContent = headMatch[1];
    
    // Find all script tags with src attributes
    const scriptRegex = /<script[^>]*\ssrc=["']([^"']+)["'][^>]*>/gi;
    let match;
    
    while ((match = scriptRegex.exec(headContent)) !== null) {
        scripts.push(match[1]);
    }
    
    return scripts;
};

/**
 * Get the current page's script sources from the head section
 */
const getCurrentScripts = (): string[] => {
    const scripts: string[] = [];
    const headScripts = document.head.querySelectorAll('script[src]');
    
    headScripts.forEach((script) => {
        const src = script.getAttribute('src');
        if (src) {
            scripts.push(src);
        }
    });
    
    return scripts;
};

/**
 * Fetch the latest index.html from the server with cache busting
 */
const fetchLatestIndexHtml = async (): Promise<string | null> => {
    try {
        const cacheBuster = `_cb=${Date.now()}`;
        const url = `${location.origin}/index.html?${cacheBuster}`;
        
        const response = await fetch(url, {
            method: 'GET',
            cache: 'no-store',
            headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache'
            }
        });
        
        if (!response.ok) {
            console.error('VersionCheck: Failed to fetch index.html', response.status);
            return null;
        }
        
        return await response.text();
    } catch (error) {
        console.error('VersionCheck: Error fetching index.html', error);
        return null;
    }
};

/**
 * Compare current scripts with server scripts
 * Returns true if they match (app is up to date)
 */
const compareScripts = (currentScripts: string[], serverScripts: string[]): boolean => {
    // Normalize scripts by removing query strings for comparison
    const normalize = (src: string): string => {
        // Remove query strings but keep the hash-based versioning that Vite adds
        // Vite uses filenames like: /assets/index-abc123.js
        return src.split('?')[0];
    };
    
    const currentNormalized = currentScripts.map(normalize).sort();
    const serverNormalized = serverScripts.map(normalize).sort();
    
    // If lengths differ, definitely different
    if (currentNormalized.length !== serverNormalized.length) {
        console.debug('VersionCheck: Script count differs', {
            current: currentNormalized.length,
            server: serverNormalized.length
        });
        return false;
    }
    
    // Compare each script
    for (let i = 0; i < currentNormalized.length; i++) {
        if (currentNormalized[i] !== serverNormalized[i]) {
            console.debug('VersionCheck: Script mismatch', {
                current: currentNormalized[i],
                server: serverNormalized[i]
            });
            return false;
        }
    }
    
    return true;
};

/**
 * Show the update available dialog
 */
const showUpdateDialog = async (): Promise<void> => {
    if (updateDialogShown) return;
    
    updateDialogShown = true;
    
    const result = await Swal.fire({
        title: 'Update Available',
        text: 'There is a new version of the app available. It will be downloaded now.',
        icon: 'info',
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false
    });
    
    if (result.isConfirmed) {
        // Hard refresh the page
        window.location.reload();
    }
};

/**
 * Perform the version check
 */
const checkVersion = async (): Promise<void> => {
    // Only run on live .com domains
    if (!isLiveDomain()) {
        console.debug('VersionCheck: Skipping - not on live domain');
        return;
    }
    
    // Don't check again if we've already shown the dialog
    if (updateDialogShown) {
        return;
    }
    
    console.debug('VersionCheck: Checking for updates...');
    
    // Get current scripts
    const currentScripts = getCurrentScripts();
    
    // Fetch latest index.html from server
    const serverHtml = await fetchLatestIndexHtml();
    if (!serverHtml) {
        console.debug('VersionCheck: Could not fetch server index.html');
        return;
    }
    
    // Extract scripts from server response
    const serverScripts = extractScriptSrcs(serverHtml);
    
    console.debug('VersionCheck: Comparing scripts', {
        current: currentScripts,
        server: serverScripts
    });
    
    // Compare scripts
    const isUpToDate = compareScripts(currentScripts, serverScripts);
    
    if (!isUpToDate) {
        console.info('VersionCheck: New version available!');
        await showUpdateDialog();
    } else {
        console.debug('VersionCheck: App is up to date');
    }
};

/**
 * Start the version check loop
 */
const startVersionCheck = (): void => {
    if (isRunning) {
        console.debug('VersionCheck: Already running');
        return;
    }
    
    // Only start on live domains
    if (!isLiveDomain()) {
        console.debug('VersionCheck: Not starting - not on live domain');
        return;
    }
    
    isRunning = true;
    console.debug('VersionCheck: Starting version check loop');
    
    // Run the first check immediately on app load
    checkVersion().then(() => {
        // Then schedule the recurring checks
        if (isRunning && !updateDialogShown) {
            versionCheckTimerId = setTimeout(async () => {
                await runVersionCheckLoop();
            }, VERSION_CHECK_INTERVAL_MS);
        }
    });
};

/**
 * The version check loop
 */
const runVersionCheckLoop = async (): Promise<void> => {
    if (!isRunning) return;
    
    await checkVersion();
    
    // Schedule next check
    if (isRunning && !updateDialogShown) {
        versionCheckTimerId = setTimeout(async () => {
            await runVersionCheckLoop();
        }, VERSION_CHECK_INTERVAL_MS);
    }
};

/**
 * Stop the version check loop
 */
const stopVersionCheck = (): void => {
    if (versionCheckTimerId) {
        clearTimeout(versionCheckTimerId);
        versionCheckTimerId = null;
    }
    isRunning = false;
    console.debug('VersionCheck: Stopped');
};

/**
 * Force a version check immediately
 */
const forceCheck = async (): Promise<void> => {
    await checkVersion();
};

export default {
    start: startVersionCheck,
    stop: stopVersionCheck,
    check: forceCheck,
    isLiveDomain
};
